import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { Toaster, toast } from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

import Pagination from "../components/Pagination";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import CustomerDetailsPDF from "../components/CustomerDetailsPDF";
import { generateCustomerLedgerPdfBlob } from "../utils/generateCustomerLedgerPdf";
import { Icon } from "@iconify/react";
import ViewSalesInvoice from "./ViewSalesInvoice";
import ViewOldPurchase from "./ViewOldPurchase";
import ViewCustomOrder from "./ViewCustomOrder";
import ViewRepairingOrder from "./ViewRepairingOrder";
import ViewCashbookVoucher from "./ViewCashbookVoucher";
import {
  isSalesLedgerRow,
  extractSalesVoucherNumber,
} from "../components/SalesInvoicePrintModal";
import { resolveSaleIsQuote } from "../utils/salesErpAdapter";
import {
  isCustomOrderLedgerRow,
  isRepairLedgerRow,
  isCashbookVoucherLedgerRow,
  extractCustomOrderId,
  extractCustomOrderDocNumber,
  extractRepairingOrderId,
  extractRepairDocNumber,
  extractCashbookVoucherShowId,
  resolveCashbookVoucherIsQuote,
  resolveCustomOrderIsQuote,
  resolveRepairingOrderIsQuote,
} from "../utils/customerLedgerDocTypes";
import {
  isErpApiSuccess,
  listOldMetalPurchases,
  listCustomOrders,
  listRepairingOrders,
  postUserLedger,
  getErpRequestErrorMessage,
} from "../erpClient";
import {
  isOldMetalApiSuccess,
  normalizeOldMetalListRows,
} from "../utils/oldMetalPurchaseErpAdapter";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  buildReferenceSelectOptions,
  customerSelectMenuProps,
  fetchCustomerUserSearch,
} from "../utils/customerUserSelectOptions";

function hasFilledValue(value) {
  return value != null && String(value).trim() !== "";
}

function sortLedgerEntries(entries) {
  return [...entries].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    if (dateA.getTime() === dateB.getTime()) return 0;
    return dateA - dateB;
  });
}

function processCustomerLedgerData(entries, closingFromApi) {
  const sortedEntries = sortLedgerEntries(entries);
  const useApiBalance =
    hasFilledValue(closingFromApi) &&
    sortedEntries.every((entry) => hasFilledValue(entry.balance));

  let amountBalance = 0;
  return sortedEntries.map((entry) => {
    const amountCredit = parseFloat(entry.aavak_amt) || 0;
    const amountDebit = parseFloat(entry.jaavak_amt) || 0;
    if (!useApiBalance) {
      amountBalance += amountCredit - amountDebit;
    }
    const balanceValue = useApiBalance ? entry.balance : amountBalance;
    return {
      date: entry.date ? formatDisplayDateDDMMYYYY(entry.date) : "-",
      credit: amountCredit ? formatIndianCurrency(amountCredit) : "",
      debit: amountDebit ? formatIndianCurrency(amountDebit) : "",
      balance: formatIndianBalance(balanceValue),
      remarks: entry.remarks || "",
      type: entry.type || "",
      rawEntry: entry,
    };
  });
}

function formatLedgerAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0.00";
  return formatIndianBalance(value);
}

function formatDisplayDateDDMMYYYY(dateString) {
  if (!dateString || dateString === "-") return "-";
  const str = String(dateString).trim();
  if (str.includes("/")) return str;
  const parts = str.split(" ")[0].split("-");
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    if (parts[2].length === 4) {
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    }
  }
  return dateString;
}

function formatIndianCurrency(value) {
  if (value === "" || value == null || value === "-") return "-";
  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
  if (isNaN(num)) return "-";
  if (num === 0) return "-";
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `-${formatted}` : formatted;
}

function formatIndianBalance(value) {
  if (value === "" || value == null || value === "-") return "-";
  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
  if (isNaN(num)) return "-";
  if (num === 0) return "0.00";
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `-${formatted}` : formatted;
}

function formatLedgerWeight(value) {
  const weight = Number(value);
  if (!Number.isFinite(weight) || weight === 0) return "";
  return weight.toFixed(3);
}

function numGm(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function pickEntryBalanceField(entry, keys) {
  for (const key of keys) {
    if (hasFilledValue(entry?.[key])) return entry[key];
  }
  return null;
}

/**
 * user_ledger entries expose gold_gm / silver_gm (and wholesale total_out_gm /
 * old_metal_in_gm / paid_gm). Karigar-style gold_aavak / gold_jaavak are preferred
 * when present.
 */
function pickMetalCreditDebit(entry, metal) {
  const aavakKey = `${metal}_aavak`;
  const jaavakKey = `${metal}_jaavak`;
  const aavakAlt = `aavak_${metal}`;
  const jaavakAlt = `jaavak_${metal}`;

  if (
    entry?.[aavakKey] != null ||
    entry?.[jaavakKey] != null ||
    entry?.[aavakAlt] != null ||
    entry?.[jaavakAlt] != null
  ) {
    return {
      credit: numGm(entry[aavakKey] ?? entry[aavakAlt]),
      debit: numGm(entry[jaavakKey] ?? entry[jaavakAlt]),
    };
  }

  const metalType = String(entry?.metal_type || "").toUpperCase();
  const hasWhBreakdown =
    entry?.total_out_gm != null ||
    entry?.old_metal_in_gm != null ||
    entry?.paid_gm != null;

  if (hasWhBreakdown) {
    const appliesToMetal =
      (metal === "gold" && (!metalType || metalType.includes("GOLD"))) ||
      (metal === "silver" && metalType.includes("SILVER"));
    if (appliesToMetal) {
      const outGm = numGm(entry.total_out_gm);
      const inGm = numGm(entry.old_metal_in_gm) + numGm(entry.paid_gm);
      if (outGm > 0 || inGm > 0) {
        return { credit: inGm, debit: outGm };
      }
      // Breakdown present but zeros — fall through to gold_gm / silver_gm.
    }
  }

  const gm = numGm(entry[`${metal}_gm`] ?? (metal === "gold" ? entry.gm : 0));
  if (gm === 0) return { credit: 0, debit: 0 };

  const entryType = String(entry?.entry_type || "").toLowerCase();
  const docType = String(entry?.doc_type || "").toLowerCase();
  const amountCredit = numGm(entry?.aavak_amt);
  const amountDebit = numGm(entry?.jaavak_amt);

  const isCreditMetal =
    /pay|receipt|aavak|credit|return|urd|old_?metal|old_?purchase/.test(
      entryType,
    ) ||
    (amountCredit > 0 && amountDebit === 0);

  const isDebitMetal =
    entryType.includes("due") ||
    ["sale", "whsale", "wholesale"].includes(docType) ||
    /^(sale|whsale|wholesale)/.test(entryType) ||
    (amountDebit > 0 && amountCredit === 0) ||
    numGm(entry?.total_out_gm) > 0 ||
    numGm(entry?.baki_gm) > 0;

  if (isCreditMetal && !isDebitMetal) {
    return { credit: gm, debit: 0 };
  }
  return { credit: 0, debit: gm };
}

/** Negative closing balance is the cash-in due. Zero or credit is no due. */
function cashInDueFromClosingBalance(closingBalance) {
  const amount = Number(closingBalance);
  if (!Number.isFinite(amount) || amount >= 0) return 0;
  return Math.abs(amount);
}

function balanceCellColor(value, isOpening) {
  if (isOpening) return undefined;
  const n = parseFloat(value || 0);
  if (n < 0) return "#dc3545";
  if (n > 0) return "#28a745";
  return undefined;
}

function isUrdLedgerRow(row) {
  if (!row || row.isOpening) return false;
  const typeStr = String(row.type || "").trim();
  // "CASH - URD_31" style settlements — open the URD row, not this payment
  if (
    /^(CASH|BANK|UPI|CARD|CHEQUE)\s*[-:]/i.test(typeStr) &&
    /\b(URD|PUR)[_-]/i.test(typeStr)
  ) {
    return false;
  }
  const t = typeStr.toLowerCase();
  if (
    t.startsWith("urd") ||
    t.startsWith("old_purchase") ||
    t.startsWith("old purchase") ||
    t.startsWith("old_metal") ||
    t.startsWith("old metal")
  ) {
    return true;
  }
  if (
    row.rawEntry?.urd_document_id ||
    row.rawEntry?.old_purchase_id ||
    row.rawEntry?.purchase_id
  ) {
    if (!/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(typeStr)) return true;
  }
  if ((t.includes("urd_") || t.includes("pur_")) && !t.includes("sal_")) {
    if (!/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(typeStr)) return true;
  }
  return false;
}

function extractUrdDocNumber(row) {
  if (!row) return "";
  if (row.rawEntry?.urd_doc_number) return String(row.rawEntry.urd_doc_number).trim();
  if (row.rawEntry?.purchase_no) return String(row.rawEntry.purchase_no).trim();
  if (row.rawEntry?.doc_number) return String(row.rawEntry.doc_number).trim();
  if (
    row.rawEntry?.voucher_no &&
    !String(row.rawEntry.voucher_no).toUpperCase().startsWith("SAL_")
  ) {
    return String(row.rawEntry.voucher_no).trim();
  }

  const typeStr = String(row.type || "").trim();
  const typeMatch = typeStr.match(/(?:urd|old[_-]?purchase)\s*[-:]\s*([A-Za-z0-9_-]+)/i);
  if (typeMatch && typeMatch[1]) return typeMatch[1].trim();

  const codeMatch = typeStr.match(/((?:URD|PUR)[_-][A-Za-z0-9_-]+)/i);
  if (codeMatch && codeMatch[1]) return codeMatch[1].trim();

  const remarksStr = String(row.remarks || "").trim();
  const remarkMatch = remarksStr.match(/((?:URD|PUR)[_-][A-Za-z0-9_-]+)/i);
  if (remarkMatch && remarkMatch[1]) return remarkMatch[1].trim();

  return "";
}

function CustomerLedger({
  masterGoldRate,
  masterSilverRate,
  showLiveRate = false,
} = {}) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [loadingCustomerSearch, setLoadingCustomerSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfPreviewKey, setPdfPreviewKey] = useState(0);
  const [printLoading, setPrintLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);
  const [closingBalance, setClosingBalance] = useState(null);
  const [closingGoldBalance, setClosingGoldBalance] = useState(null);
  const [closingSilverBalance, setClosingSilverBalance] = useState(null);
  const [showEditSaleModal, setShowEditSaleModal] = useState(false);
  const [editSaleId, setEditSaleId] = useState(null);
  const [editSaleInvoices, setEditSaleInvoices] = useState([]);
  const [editSaleIsQuote, setEditSaleIsQuote] = useState(undefined);
  const [showUrdModal, setShowUrdModal] = useState(false);
  const [viewUrdId, setViewUrdId] = useState(null);
  const [viewUrdNo, setViewUrdNo] = useState("");
  const [showCustomOrderModal, setShowCustomOrderModal] = useState(false);
  const [viewCustomOrderId, setViewCustomOrderId] = useState(null);
  const [viewCustomOrderIsQuote, setViewCustomOrderIsQuote] =
    useState(undefined);
  const [viewCustomOrderDoc, setViewCustomOrderDoc] = useState("");
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [viewRepairId, setViewRepairId] = useState(null);
  const [viewRepairIsQuote, setViewRepairIsQuote] = useState(undefined);
  const [viewRepairDoc, setViewRepairDoc] = useState("");
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [viewVoucherId, setViewVoucherId] = useState(null);
  const [viewVoucherIsQuote, setViewVoucherIsQuote] = useState(undefined);
  const customerSearchTimerRef = useRef(null);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());
  const selectedCustomerRef = useRef(selectedCustomer);
  selectedCustomerRef.current = selectedCustomer;

  const scheduleCustomerSearch = (rawInput) => {
    clearTimeout(customerSearchTimerRef.current);
    setCustomerSearchInput(rawInput);
    if (!String(rawInput ?? "").trim()) {
      setCustomerOptions([]);
      setLoadingCustomerSearch(false);
      return;
    }
    setLoadingCustomerSearch(true);
    customerSearchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(rawInput);
        setCustomerOptions(buildReferenceSelectOptions(rows));
      } catch {
        setCustomerOptions([]);
        toast.error("Failed to fetch customers");
      } finally {
        setLoadingCustomerSearch(false);
      }
    }, 400);
  };

  useEffect(() => {
    return () => {
      clearTimeout(customerSearchTimerRef.current);
    };
  }, []);

  // Listen for API URL changes and reset customer search + ledger
  useEffect(() => {
    const handleApiUrlChange = () => {
      clearTimeout(customerSearchTimerRef.current);
      setSelectedCustomer(null);
      setCustomerOptions([]);
      setCustomerSearchInput("");
      setLoadingCustomerSearch(false);
      setLedgerRows([]);
      setPdfData(null);
      setClosingBalance(null);
      setClosingGoldBalance(null);
      setClosingSilverBalance(null);
      setTotalCount(0);
      setCurrentPage(1);
      pageCacheRef.current.clear();
      setShowPDFModal(false);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const fetchCustomerLedger = useCallback(
    async ({
      page = 1,
      limit = 10,
      silent = false,
      bypassCache = false,
    } = {}) => {
      const customer = selectedCustomerRef.current;
      if (!customer?.value) return;

      const cacheKey = `${customer.value}:${entireReport ? "entire" : `${fromDate}|${toDate}`}:${page}:${limit}`;
      if (!bypassCache) {
        const cached = pageCacheRef.current.get(cacheKey);
        if (cached) {
          setLedgerRows(cached.ledgerRows);
          setPdfData(cached.pdfData);
          setClosingBalance(cached.closingBalance);
          setClosingGoldBalance(cached.closingGoldBalance);
          setClosingSilverBalance(cached.closingSilverBalance);
          setCurrentPage(cached.currentPage);
          setTotalCount(cached.totalCount);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setClosingBalance(null);
      setClosingGoldBalance(null);
      setClosingSilverBalance(null);

      const apiPayload = {
        uid: customer.value,
        page,
        limit,
      };
      if (entireReport) {
        apiPayload.type = "entire";
      } else {
        if (fromDate) apiPayload.from_date = fromDate;
        if (toDate) apiPayload.to_date = toDate;
      }

      try {
        const payload = await postUserLedger(apiPayload);

        if (
          !payload ||
          (!isErpApiSuccess(payload) && payload.message !== "radha")
        ) {
          toast.error(
            payload?.message || "Failed to fetch ledger data. Please try again.",
          );
          setLedgerRows([]);
          setPdfData(null);
          setClosingBalance(null);
          setClosingGoldBalance(null);
          setClosingSilverBalance(null);
          setTotalCount(0);
          return;
        }

        const data = payload.data || {};
        const entries = Array.isArray(data.entries) ? data.entries : [];
        const closingFromApi = hasFilledValue(data.closing_balance)
          ? data.closing_balance
          : null;
        const closingGoldFromApi = hasFilledValue(
          data.closing_gold_balance ?? data.closing_gold,
        )
          ? data.closing_gold_balance ?? data.closing_gold
          : null;
        const closingSilverFromApi = hasFilledValue(
          data.closing_silver_balance ?? data.closing_silver,
        )
          ? data.closing_silver_balance ?? data.closing_silver
          : null;

        if (entries.length === 0) {
          if (!silent) {
            toast.error(
              "No ledger entries found for this Customer / date range.",
            );
          }
          setLedgerRows([]);
          setPdfData(null);
          setClosingBalance(null);
          setClosingGoldBalance(null);
          setClosingSilverBalance(null);
          setTotalCount(0);
          setCurrentPage(1);
          return;
        }

        const sortedEntries = sortLedgerEntries(entries);
        const useApiAmountBalance =
          hasFilledValue(closingFromApi) &&
          sortedEntries.every((entry) => hasFilledValue(entry.balance));
        const useApiGoldBalance =
          hasFilledValue(closingGoldFromApi) &&
          sortedEntries.every((entry) =>
            hasFilledValue(
              pickEntryBalanceField(entry, [
                "gold_balance",
                "total_gold",
                "balance_gold",
              ]),
            ),
          );
        const useApiSilverBalance =
          hasFilledValue(closingSilverFromApi) &&
          sortedEntries.every((entry) =>
            hasFilledValue(
              pickEntryBalanceField(entry, [
                "silver_balance",
                "total_silver",
                "balance_silver",
              ]),
            ),
          );

        let goldBalance = 0;
        let silverBalance = 0;
        let amountBalance = 0;
        const ledgerData = sortedEntries.map((entry) => {
          const { credit: goldCredit, debit: goldDebit } = pickMetalCreditDebit(
            entry,
            "gold",
          );
          const { credit: silverCredit, debit: silverDebit } =
            pickMetalCreditDebit(entry, "silver");
          const amountCredit = parseFloat(entry.aavak_amt) || 0;
          const amountDebit = parseFloat(entry.jaavak_amt) || 0;

          if (!useApiGoldBalance) {
            goldBalance += goldCredit - goldDebit;
          }
          if (!useApiSilverBalance) {
            silverBalance += silverCredit - silverDebit;
          }
          if (!useApiAmountBalance) {
            amountBalance += amountCredit - amountDebit;
          }

          const goldBalanceValue = useApiGoldBalance
            ? pickEntryBalanceField(entry, [
                "gold_balance",
                "total_gold",
                "balance_gold",
              ])
            : goldBalance;
          const silverBalanceValue = useApiSilverBalance
            ? pickEntryBalanceField(entry, [
                "silver_balance",
                "total_silver",
                "balance_silver",
              ])
            : silverBalance;
          const amountBalanceValue = useApiAmountBalance
            ? entry.balance
            : amountBalance;

          return {
            date: entry.date ? formatDisplayDateDDMMYYYY(entry.date) : "-",
            // Gold gm (Karigar-compatible keys for PDF reuse patterns)
            credit: formatLedgerWeight(goldCredit),
            debit: formatLedgerWeight(goldDebit),
            balance: formatLedgerWeight(goldBalanceValue) || "0.000",
            silver_credit: formatLedgerWeight(silverCredit),
            silver_debit: formatLedgerWeight(silverDebit),
            silver_balance:
              formatLedgerWeight(silverBalanceValue) || "0.000",
            amount_credit: amountCredit
              ? formatLedgerAmount(amountCredit)
              : "",
            amount_debit: amountDebit
              ? formatLedgerAmount(amountDebit)
              : "",
            amount_balance: formatLedgerAmount(amountBalanceValue),
            remarks: entry.remarks || "",
            type: entry.type || "",
            rawEntry: entry,
          };
        });

        const paginationMeta = parseApiPagination(payload, ledgerData.length);
        const resolvedClosing = useApiAmountBalance
          ? closingFromApi
          : amountBalance;
        const resolvedClosingGold = useApiGoldBalance
          ? closingGoldFromApi
          : goldBalance;
        const resolvedClosingSilver = useApiSilverBalance
          ? closingSilverFromApi
          : silverBalance;

        const nextPdf = {
          ledgerData,
          customerInfo: {
            name: customer?.label || "",
            mobile: customer?.mobile || "",
            city: customer?.city || "",
            address: customer?.address || "",
          },
          fromDate,
          toDate,
          entireReport,
          currentPage: paginationMeta.page,
          totalCount: paginationMeta.total,
        };

        setLedgerRows(ledgerData);
        setPdfData(nextPdf);
        setClosingBalance(resolvedClosing);
        setClosingGoldBalance(resolvedClosingGold);
        setClosingSilverBalance(resolvedClosingSilver);
        setCurrentPage(paginationMeta.page);
        setTotalCount(paginationMeta.total);
        if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);

        pageCacheRef.current.set(cacheKey, {
          ledgerRows: ledgerData,
          pdfData: nextPdf,
          closingBalance: resolvedClosing,
          closingGoldBalance: resolvedClosingGold,
          closingSilverBalance: resolvedClosingSilver,
          currentPage: paginationMeta.page,
          totalCount: paginationMeta.total,
        });

        if (!silent) {
          toast.success("Ledger generated successfully!");
        }
      } catch (error) {
        toast.error(
          getErpRequestErrorMessage(
            error,
            "An error occurred while fetching the ledger data.",
          ),
        );
        console.error("Error fetching customer ledger:", error);
        setLedgerRows([]);
        setPdfData(null);
        setClosingBalance(null);
        setClosingGoldBalance(null);
        setClosingSilverBalance(null);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [entireReport, fromDate, toDate],
  );

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!selectedCustomer?.value) return;
    pageCacheRef.current.clear();
    setCurrentPage(1);
    await fetchCustomerLedger({
      page: 1,
      limit: itemsPerPage,
      bypassCache: true,
    });
  };

  const handlePageChange = (page) => {
    if (ignoreNextPageFetchRef.current) {
      ignoreNextPageFetchRef.current = false;
      return;
    }
    setCurrentPage(page);
    fetchCustomerLedger({ page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    ignoreNextPageFetchRef.current = true;
    pageCacheRef.current.clear();
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchCustomerLedger({ page: 1, limit, silent: true, bypassCache: true });
  };

  const cashInDue = cashInDueFromClosingBalance(closingBalance);
  const goldInDue = cashInDueFromClosingBalance(closingGoldBalance);
  const silverInDue = cashInDueFromClosingBalance(closingSilverBalance);

  const handlePrint = async () => {
    const customer = selectedCustomerRef.current || selectedCustomer;
    if (!customer?.value) {
      toast.error("Please select a customer first.");
      return;
    }
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      uid: customer.value,
      page: 0,
      limit: 0,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.filter_type = "entire";
    } else {
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postUserLedger(printPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message || "Failed to fetch ledger data for printing. Please try again."
        );
        return;
      }

      const data = payload.data || {};
      const entries = Array.isArray(data.entries) ? data.entries : [];
      const closingFromApi = hasFilledValue(data.closing_balance)
        ? data.closing_balance
        : null;

      if (entries.length === 0) {
        toast.error("No ledger entries found to print.");
        return;
      }

      const processedLedgerData = processCustomerLedgerData(entries, closingFromApi);

      const customerInfo = {
        name: customer?.label || "",
        mobile: customer?.mobile || "",
        city: customer?.city || "",
        address: customer?.address || "",
      };

      const nextPdf = {
        ledgerData: processedLedgerData,
        customerInfo,
        fromDate,
        toDate,
        entireReport,
      };
      setPdfData(nextPdf);

      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      const blob = await generateCustomerLedgerPdfBlob({
        ledgerData: processedLedgerData,
        customerInfo,
        fromDate,
        toDate,
        entireReport,
      });
      const _unusedBlob = null; if (false) await pdf(
        <CustomerDetailsPDF
          ledgerData={processedLedgerData}
          customerInfo={customerInfo}
          fromDate={fromDate}
          toDate={toDate}
          entireReport={entireReport}
        />
      ).toBlob();

      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlobUrl(blobUrl);
      setShowPDFModal(true);
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data."
        )
      );
      console.error("Error generating customer ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  // oldHandlePrint removed
  const _unusedPrint = () => {
    if (!ledgerRows.length) {
      toast.error("Please generate the ledger first.");
      return;
    }
    setPdfData({
      ledgerData: ledgerRows,
      customerInfo: {
        name: selectedCustomer?.label || "",
        mobile: selectedCustomer?.mobile || "",
        city: selectedCustomer?.city || "",
        address: selectedCustomer?.address || "",
      },
      fromDate,
      toDate,
      entireReport,
      currentPage,
      totalCount,
    });
    setPdfPreviewKey((k) => k + 1);
    setShowPDFModal(true);
  };

  const closeEditSaleModal = useCallback(() => {
    setShowEditSaleModal(false);
    setEditSaleId(null);
    setEditSaleInvoices([]);
    setEditSaleIsQuote(undefined);
  }, []);

  const handleSaleClick = (row) => {
    const saleId =
      row.rawEntry?.document_id ||
      row.rawEntry?.sale_id ||
      row.rawEntry?.sales_id ||
      row.rawEntry?.id;
    const voucherNumber = extractSalesVoucherNumber(row);
    const isQuote = resolveSaleIsQuote({
      voucherNumber,
      doc_number: row.rawEntry?.doc_number,
      estimate: row.rawEntry?.estimate,
      is_quote: row.rawEntry?.is_quote,
    });

    if (!saleId) {
      toast.error("Invoice details not available.");
      return;
    }

    setEditSaleId(saleId);
    setEditSaleIsQuote(isQuote);
    setEditSaleInvoices([
      {
        id: saleId,
        voucherNumber: voucherNumber || "",
        isQuote,
      },
    ]);
    setShowEditSaleModal(true);
  };

  const closeUrdModal = useCallback(() => {
    setShowUrdModal(false);
    setViewUrdId(null);
    setViewUrdNo("");
  }, []);

  const closeCustomOrderModal = useCallback(() => {
    setShowCustomOrderModal(false);
    setViewCustomOrderId(null);
    setViewCustomOrderIsQuote(undefined);
    setViewCustomOrderDoc("");
  }, []);

  const closeRepairModal = useCallback(() => {
    setShowRepairModal(false);
    setViewRepairId(null);
    setViewRepairIsQuote(undefined);
    setViewRepairDoc("");
  }, []);

  const closeVoucherModal = useCallback(() => {
    setShowVoucherModal(false);
    setViewVoucherId(null);
    setViewVoucherIsQuote(undefined);
  }, []);

  const handleUrdClick = async (row) => {
    const docNo = extractUrdDocNumber(row);
    let targetId =
      row.rawEntry?.old_purchase_id ||
      row.rawEntry?.urd_document_id ||
      row.rawEntry?.purchase_id ||
      (String(row.rawEntry?.doc_type || "").toUpperCase() === "OLD_PURCHASE"
        ? row.rawEntry?.document_id
        : null);

    if (!targetId && docNo) {
      try {
        const res = await listOldMetalPurchases({ search: docNo });
        if (isOldMetalApiSuccess(res)) {
          const rows = normalizeOldMetalListRows(res);
          const match =
            rows.find(
              (r) =>
                String(r.purchase_no || "").trim().toUpperCase() ===
                  docNo.toUpperCase() ||
                String(r.id || "").trim() === docNo,
            ) || rows[0];
          if (match?.id) targetId = match.id;
        }
      } catch (err) {
        console.warn("Error finding purchase ID for URD:", err);
      }
    }

    if (!targetId && row.rawEntry?.document_id) {
      targetId = row.rawEntry.document_id;
    }

    if (!targetId) {
      toast.error("URD details not available.");
      return;
    }

    setViewUrdId(targetId);
    setViewUrdNo(docNo || "");
    setShowUrdModal(true);
  };

  const handleCustomOrderClick = async (row) => {
    let orderId = extractCustomOrderId(row);
    const docNo = extractCustomOrderDocNumber(row);
    const isQuote = resolveCustomOrderIsQuote({
      doc_number: docNo,
      type: row.type,
      co_number: row.rawEntry?.co_number,
      order_number: row.rawEntry?.order_number,
      estimate: row.rawEntry?.estimate,
      is_quote: row.rawEntry?.is_quote,
    });

    if (!orderId && docNo) {
      try {
        const res = await listCustomOrders({
          search: docNo,
          limit: 10,
          isQuote,
        });
        const list =
          res?.data?.custom_orders ||
          res?.data?.data ||
          res?.data?.records ||
          res?.data ||
          [];
        if (Array.isArray(list) && list.length > 0) {
          const match =
            list.find(
              (item) =>
                String(
                  item.order_number || item.doc_number || item.co_number || "",
                )
                  .trim()
                  .toUpperCase() === docNo.toUpperCase() ||
                String(item.id || "").trim() === docNo,
            ) || list[0];
          if (match?.id) orderId = match.id;
        }
      } catch (err) {
        console.warn("Error finding custom order id:", err);
      }
    }

    if (!orderId) {
      toast.error("Custom order details not available.");
      return;
    }

    setViewCustomOrderId(orderId);
    setViewCustomOrderIsQuote(isQuote);
    setViewCustomOrderDoc(docNo || "");
    setShowCustomOrderModal(true);
  };

  const handleRepairClick = async (row) => {
    let orderId = extractRepairingOrderId(row);
    const docNo = extractRepairDocNumber(row);
    const isQuote = resolveRepairingOrderIsQuote({
      doc_number: docNo,
      type: row.type,
      token_no: row.rawEntry?.token_no,
      order_number: row.rawEntry?.order_number,
      estimate: row.rawEntry?.estimate,
      is_quote: row.rawEntry?.is_quote,
    });

    if (!orderId && docNo) {
      try {
        const res = await listRepairingOrders({
          search: docNo,
          limit: 10,
          isQuote,
        });
        const list =
          res?.data?.repairing_orders ||
          res?.data?.data ||
          res?.data?.records ||
          res?.data ||
          [];
        if (Array.isArray(list) && list.length > 0) {
          const match =
            list.find(
              (item) =>
                String(
                  item.order_number ||
                    item.doc_number ||
                    item.token_no ||
                    "",
                )
                  .trim()
                  .toUpperCase() === docNo.toUpperCase() ||
                String(item.id || "").trim() === docNo,
            ) || list[0];
          if (match?.id) orderId = match.id;
        }
      } catch (err) {
        console.warn("Error finding repairing order id:", err);
      }
    }

    if (!orderId) {
      toast.error("Repair order details not available.");
      return;
    }

    setViewRepairId(orderId);
    setViewRepairIsQuote(isQuote);
    setViewRepairDoc(docNo || "");
    setShowRepairModal(true);
  };

  const handleVoucherClick = (row) => {
    const id = extractCashbookVoucherShowId(row);
    if (id == null) {
      toast.error("Voucher details not available.");
      return;
    }
    const isQuote = resolveCashbookVoucherIsQuote({
      bill_type: row.rawEntry?.bill_type,
      bill_type_name: row.rawEntry?.bill_type_name,
    });
    setViewVoucherId(id);
    setViewVoucherIsQuote(isQuote);
    setShowVoucherModal(true);
  };

  const renderClickableType = (row, onClick, title) => (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none fw-semibold d-inline-flex align-items-center gap-1 border-0 bg-transparent text-start"
      style={{
        cursor: "pointer",
        color: "var(--theme-primary, #0d6efd)",
        fontSize: "inherit",
        verticalAlign: "middle",
      }}
      onClick={onClick}
      title={title}
    >
      <span className="text-decoration-underline">{row.type || "-"}</span>
      <Icon
        icon="solar:eye-bold"
        width="15"
        height="15"
        className="opacity-75 flex-shrink-0"
      />
    </button>
  );

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Customer Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* Customer Dropdown */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">Select Customer</label>
                  <Select
                    options={customerOptions}
                    value={selectedCustomer}
                    inputValue={customerSearchInput}
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        scheduleCustomerSearch(inputValue);
                      }
                      if (action === "menu-close" || action === "set-value") {
                        clearTimeout(customerSearchTimerRef.current);
                        setCustomerSearchInput("");
                        setCustomerOptions([]);
                        setLoadingCustomerSearch(false);
                      }
                    }}
                    onChange={(option) => {
                      setSelectedCustomer(option);
                      setCustomerSearchInput("");
                      setCustomerOptions([]);
                      setLoadingCustomerSearch(false);
                    }}
                    noOptionsMessage={() =>
                      loadingCustomerSearch
                        ? "Searching..."
                        : customerSearchInput.trim()
                          ? "No customers found"
                          : "Type name or mobile to search"
                    }
                    placeholder="Search and select Customer..."
                    isClearable
                    isSearchable
                    isLoading={loadingCustomerSearch}
                    filterOption={() => true}
                    {...customerSelectMenuProps}
                  />
                </div>
                {/* From Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={!selectedCustomer || loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {ledgerRows.length > 0 && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 d-flex flex-wrap align-items-center gap-2">
                    <span>
                      Ledger -{" "}
                      {selectedCustomer?.label ? selectedCustomer.label : ""}
                    </span>
                    {closingBalance != null && (
                      <span className="badge bg-danger-subtle text-danger">
                        Cash-in Due: ₹
                        {cashInDue.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    )}
                    {closingGoldBalance != null && goldInDue > 0 && (
                      <span className="badge bg-warning-subtle text-dark">
                        Gold Due: {goldInDue.toFixed(3)} gm
                      </span>
                    )}
                    {closingSilverBalance != null && silverInDue > 0 && (
                      <span className="badge bg-secondary-subtle text-dark">
                        Silver Due: {silverInDue.toFixed(3)} gm
                      </span>
                    )}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print"
                    )}
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                    <thead>
                      <tr>
                        <th colSpan="2" className="text-center"></th>
                        <th colSpan="3" className="text-center text-success">
                          Credited
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                          style={{ color: "#dc3545" }}
                        >
                          Debited
                        </th>
                        <th colSpan="3" className="text-center">
                          Balance
                        </th>
                        <th className="text-center"></th>
                      </tr>
                      <tr>
                        <th className="text-nowrap">Date</th>
                        <th className="text-nowrap">Type</th>
                        <th className="text-end text-nowrap text-success">
                          Gold (gm)
                        </th>
                        <th className="text-end text-nowrap text-success">
                          Silver (gm)
                        </th>
                        <th className="text-end text-nowrap text-success">
                          Amount
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Gold (gm)
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Silver (gm)
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Amount
                        </th>
                        <th className="text-end text-nowrap">Gold (gm)</th>
                        <th className="text-end text-nowrap">Silver (gm)</th>
                        <th className="text-end text-nowrap">Amount</th>
                        <th className="text-nowrap">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => {
                        const cellStyle = row.isOpening
                          ? { backgroundColor: "#E3F2FD", fontWeight: 500 }
                          : undefined;
                        const dateValue = row.isOpening
                          ? "-"
                          : row.date
                            ? String(row.date).split(" ")[0]
                            : "-";
                        return (
                          <tr key={idx}>
                            <td style={cellStyle}>{dateValue}</td>
                            <td style={cellStyle}>
                              {isCashbookVoucherLedgerRow(row)
                                ? renderClickableType(
                                    row,
                                    () => handleVoucherClick(row),
                                    "View cash/bank voucher",
                                  )
                                : isSalesLedgerRow(row)
                                  ? renderClickableType(
                                      row,
                                      () => handleSaleClick(row),
                                      "View sales bill",
                                    )
                                  : isUrdLedgerRow(row)
                                    ? renderClickableType(
                                        row,
                                        () => handleUrdClick(row),
                                        "View old metal purchase",
                                      )
                                    : isCustomOrderLedgerRow(row)
                                      ? renderClickableType(
                                          row,
                                          () => handleCustomOrderClick(row),
                                          "View custom order",
                                        )
                                      : isRepairLedgerRow(row)
                                        ? renderClickableType(
                                            row,
                                            () => handleRepairClick(row),
                                            "View repair order",
                                          )
                                        : row.type || "-"}
                            </td>
                            <td
                              className={`text-end ${
                                row.credit ? "text-success" : ""
                              }`}
                              style={cellStyle}
                            >
                              {row.credit || "-"}
                            </td>
                            <td
                              className={`text-end ${
                                row.silver_credit ? "text-success" : ""
                              }`}
                              style={cellStyle}
                            >
                              {row.silver_credit || "-"}
                            </td>
                            <td
                              className={`text-end ${
                                row.amount_credit ? "text-success" : ""
                              }`}
                              style={cellStyle}
                            >
                              {row.amount_credit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.silver_debit
                                  ? "#dc3545"
                                  : undefined,
                              }}
                            >
                              {row.silver_debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.amount_debit
                                  ? "#dc3545"
                                  : undefined,
                              }}
                            >
                              {row.amount_debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: balanceCellColor(
                                  row.balance,
                                  row.isOpening,
                                ),
                              }}
                            >
                              {row.balance || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: balanceCellColor(
                                  row.silver_balance,
                                  row.isOpening,
                                ),
                              }}
                            >
                              {row.silver_balance || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: balanceCellColor(
                                  row.amount_balance,
                                  row.isOpening,
                                ),
                              }}
                            >
                              {row.amount_balance || "-"}
                            </td>
                            <td style={cellStyle}>{row.remarks || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemsPerPageOptions={[10, 20, 50, 100]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing Customer Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Customer Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              title="Customer Ledger PDF Preview"
              src={pdfBlobUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : showPDFModal && pdfData?.ledgerData?.length > 0 ? (
            <PDFViewer
              key={pdfPreviewKey}
              width="100%"
              height="100%"
              showToolbar
            >
              <CustomerDetailsPDF
                ledgerData={pdfData.ledgerData}
                customerInfo={pdfData.customerInfo}
                fromDate={pdfData.fromDate}
                toDate={pdfData.toDate}
                entireReport={pdfData.entireReport}
              />
            </PDFViewer>
          ) : null}
        </Modal.Body>
      </Modal>
      {/* View Sale Modal */}
      <Modal
        show={showEditSaleModal}
        onHide={() => closeEditSaleModal()}
        size="xl"
        centered
        backdrop="static"
        scrollable
        dialogClassName="edit-sale-from-ledger-modal"
      >
        <Modal.Body className="p-0">
          {showEditSaleModal && editSaleId != null && (
            <ViewSalesInvoice
              key={`${editSaleId}-${String(editSaleIsQuote)}`}
              saleId={editSaleId}
              selectedItemIds={[editSaleId]}
              selectedInvoices={editSaleInvoices}
              onClose={closeEditSaleModal}
              masterGoldRate={masterGoldRate}
              masterSilverRate={masterSilverRate}
              showLiveRate={showLiveRate}
              isQuote={editSaleIsQuote}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* View URD / Old Purchase */}
      <Modal
        show={showUrdModal}
        onHide={closeUrdModal}
        size="xl"
        centered
        backdrop="static"
        scrollable
        dialogClassName="edit-sale-from-ledger-modal"
      >
        <Modal.Body className="p-0">
          {showUrdModal && viewUrdId != null && (
            <ViewOldPurchase
              key={String(viewUrdId)}
              purchaseId={viewUrdId}
              purchaseNo={viewUrdNo}
              onClose={closeUrdModal}
              masterGoldRate={masterGoldRate}
              masterSilverRate={masterSilverRate}
              showLiveRate={showLiveRate}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* View Custom Order */}
      <Modal
        show={showCustomOrderModal}
        onHide={closeCustomOrderModal}
        size="xl"
        centered
        backdrop="static"
        scrollable
        dialogClassName="edit-sale-from-ledger-modal"
      >
        <Modal.Body className="p-0">
          {showCustomOrderModal && viewCustomOrderId != null && (
            <ViewCustomOrder
              key={`${viewCustomOrderId}-${String(viewCustomOrderIsQuote)}`}
              customOrderId={viewCustomOrderId}
              onClose={closeCustomOrderModal}
              masterGoldRate={masterGoldRate}
              masterSilverRate={masterSilverRate}
              isQuote={viewCustomOrderIsQuote}
              docNumber={viewCustomOrderDoc}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* View Repairing Order */}
      <Modal
        show={showRepairModal}
        onHide={closeRepairModal}
        size="xl"
        centered
        backdrop="static"
        scrollable
        dialogClassName="edit-sale-from-ledger-modal"
      >
        <Modal.Body className="p-0">
          {showRepairModal && viewRepairId != null && (
            <ViewRepairingOrder
              key={`${viewRepairId}-${String(viewRepairIsQuote)}`}
              repairingOrderId={viewRepairId}
              onClose={closeRepairModal}
              masterGoldRate={masterGoldRate}
              masterSilverRate={masterSilverRate}
              isQuote={viewRepairIsQuote}
              docNumber={viewRepairDoc}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* View Cash / Bank voucher slip */}
      <ViewCashbookVoucher
        show={showVoucherModal}
        onHide={closeVoucherModal}
        voucherId={viewVoucherId}
        isQuote={viewVoucherIsQuote}
        partyInfo={
          selectedCustomer
            ? {
                name: selectedCustomer.label,
                mobile: selectedCustomer.mobile,
                address: selectedCustomer.address,
                city: selectedCustomer.city,
              }
            : null
        }
      />
    </div>
  );
}

export default CustomerLedger;
